import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPolicyPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="legal-page-container">
            <div className="legal-page-content">
                <button className="back-button" onClick={() => navigate(-1)}>
                    ← Back
                </button>

                <h1>Privacy Policy</h1>
                <p className="last-updated">Last Updated: November 22, 2025 • Version 1.0</p>

                <section>
                    <h2>Introduction</h2>
                    <p>
                        Welcome to SeedDots. We respect your privacy and are committed to protecting your personal data.
                        This privacy policy explains how we collect, use, and share information about you when you use our platform.
                    </p>
                </section>

                <section>
                    <h2>Information We Collect</h2>
                    <h3>Account Information</h3>
                    <p>When you create an account, we collect:</p>
                    <ul>
                        <li>Email address</li>
                        <li>Username and display name</li>
                        <li>Password (encrypted)</li>
                        <li>OAuth provider information (if using Google or LinkedIn login)</li>
                    </ul>

                    <h3>Activity Data</h3>
                    <ul>
                        <li>Tasks completed and points earned</li>
                        <li>Team memberships and applications</li>
                        <li>Project interests and feedback</li>
                        <li>Survey responses and questionnaire submissions</li>
                        <li>Platform usage and analytics</li>
                    </ul>
                </section>

                <section className="highlight-section">
                    <h2>⚠️ Important: How Your Email is Shared</h2>
                    <p>
                        <strong>We share your email address in specific collaboration scenarios:</strong>
                    </p>

                    <div className="info-box">
                        <h3>1. Team Formation</h3>
                        <p>
                            When you join a team or accept team applications, your email is automatically shared
                            with all team members to facilitate direct communication and collaboration.
                        </p>
                    </div>

                    <div className="info-box">
                        <h3>2. Pre-Release Projects</h3>
                        <p>
                            When you express interest in a pre-release project, your email is shared with the
                            project owner so they can notify you when the project launches and provide exclusive access.
                        </p>
                    </div>

                    <div className="info-box">
                        <h3>3. Platform Communications</h3>
                        <p>
                            We use your email for website messages related to your tasks, team activities,
                            surveys, and important platform updates.
                        </p>
                    </div>

                    <p className="disclaimer">
                        <strong>Note:</strong> By using SeedDots's team and project features, you consent to this email
                        sharing as described above. We never sell your email to third parties or use it for marketing
                        purposes outside the platform.
                    </p>
                </section>

                <section>
                    <h2>How We Use Your Information</h2>
                    <ul>
                        <li>Provide and improve our services</li>
                        <li>Facilitate team collaboration and project connections</li>
                        <li>Process points and rewards</li>
                        <li>Send important platform notifications</li>
                        <li>Analyze platform usage to improve user experience</li>
                        <li>Prevent fraud and ensure platform security</li>
                    </ul>
                </section>

                <section>
                    <h2>Data Storage and Security</h2>
                    <p>
                        Your data is stored securely using industry-standard encryption and security practices.
                        We use Supabase (built on PostgreSQL) for data storage, with the following protections:
                    </p>
                    <ul>
                        <li>Encrypted connections (SSL/TLS)</li>
                        <li>Password hashing using bcrypt</li>
                        <li>Row-level security policies</li>
                        <li>Regular security audits and updates</li>
                    </ul>
                </section>

                <section>
                    <h2>Third-Party Services</h2>
                    <p>We use the following third-party services:</p>
                    <ul>
                        <li><strong>Supabase:</strong> Database and authentication</li>
                        <li><strong>Google OAuth:</strong> Optional social login</li>
                        <li><strong>LinkedIn OAuth:</strong> Optional social login</li>
                    </ul>
                    <p>
                        These services have their own privacy policies that govern their use of your information.
                    </p>
                </section>

                <section>
                    <h2>Your Rights</h2>
                    <p>You have the following rights regarding your personal data:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of your personal data</li>
                        <li><strong>Correction:</strong> Update incorrect or incomplete information</li>
                        <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                        <li><strong>Export:</strong> Download your data in a portable format</li>
                        <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
                    </ul>
                    <p>
                        To exercise these rights, please contact us at privacy@SeedDots.com (placeholder email).
                    </p>
                </section>

                <section>
                    <h2>Data Retention</h2>
                    <p>
                        We retain your personal data for as long as your account is active or as needed to provide
                        you services. If you delete your account, we will delete your personal data within 30 days,
                        except where we are required to retain it for legal compliance.
                    </p>
                </section>

                <section>
                    <h2>Children's Privacy</h2>
                    <p>
                        Our platform is not intended for users under 18 years of age. We do not knowingly collect
                        personal information from children under 18.
                    </p>
                </section>

                <section>
                    <h2>Changes to This Policy</h2>
                    <p>
                        We may update this privacy policy from time to time. We will notify you of any material
                        changes by posting the new policy on this page and updating the "Last Updated" date.
                    </p>
                </section>

                <section>
                    <h2>Contact Us</h2>
                    <p>
                        If you have questions about this privacy policy or our privacy practices, please contact us at:
                    </p>
                    <p className="contact-info">
                        Email: seeddots@outlook.com<br />
                    </p>
                </section>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
